import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../api";

/**
 * "Ask" — chat with your finances. Read-only RAG assistant: the API grounds
 * Claude on the tenant's own data and cites the rows it used. If AI is not
 * configured/enabled for the tenant, this points the user to Settings rather
 * than failing silently.
 */

interface AiStatus {
  configured: boolean;
  enabled: boolean;
}

interface Turn {
  role: "user" | "assistant";
  content: string;
}

const SUGGESTIONS = [
  "How much do I owe Bharat?",
  "What was my total travel spend last FY?",
  "Show my largest debits this month",
  "Net worth summary",
];

export function Assistant() {
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.get<AiStatus>("/api/ai/status").then(setStatus).catch(() => setStatus(null));
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [turns, busy]);

  async function ask(question: string) {
    const q = question.trim();
    if (!q || busy) return;
    setError(null);
    const history = turns.slice(-6);
    setTurns((t) => [...t, { role: "user", content: q }]);
    setInput("");
    setBusy(true);
    try {
      const res = await api.post<{ answer: string }>("/api/ai/chat", { question: q, history });
      setTurns((t) => [...t, { role: "assistant", content: res.answer }]);
    } catch (err) {
      const msg =
        err instanceof ApiError && err.status === 409
          ? "AI is not enabled for this workspace yet."
          : err instanceof Error
            ? err.message
            : "Something went wrong.";
      setError(msg);
      setTurns((t) => [...t, { role: "assistant", content: `⚠️ ${msg}` }]);
    } finally {
      setBusy(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void ask(input);
  }

  const disabled = status ? !status.configured || !status.enabled : false;

  return (
    <div className="screen">
      <div className="screen-head">
        <h1>Ask</h1>
        <p className="muted">Chat with your finances — grounded in your own records, with citations.</p>
      </div>

      {disabled && (
        <div className="banner warn">
          AI assistant isn't enabled for this workspace.{" "}
          <Link to="/settings">Enable it in Settings →</Link>
        </div>
      )}

      <div className="chat-thread">
        {turns.length === 0 && (
          <div className="chat-empty">
            <p className="muted">Try asking:</p>
            <div className="chip-row">
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" className="chip" onClick={() => void ask(s)} disabled={disabled || busy}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className={`chat-msg ${t.role}`}>
            <div className="chat-bubble">{t.content}</div>
          </div>
        ))}
        {busy && (
          <div className="chat-msg assistant">
            <div className="chat-bubble muted">Thinking…</div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {error && !turns.length && <p className="error">{error}</p>}

      <form className="chat-input" onSubmit={onSubmit}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={disabled ? "Enable AI in Settings to ask…" : "Ask about your money…"}
          disabled={disabled || busy}
        />
        <button type="submit" disabled={disabled || busy || !input.trim()}>
          Ask
        </button>
      </form>
    </div>
  );
}
